import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from '../../assets/styles/Admin.module.scss';
import arrowIcon from '../../assets/icons/arrow.svg';
import { ROLE_MAP, BUDGET_MAP } from '../../utils/constants';
import { formatDate } from '../../utils/date';

interface InquiryDetail {
  id: string;
  raw_id: number;
  userName: string;
  company: string;
  email: string;
  phone: string;
  role: string;
  budget: string;
  message: string;
  status: string;
  date: string;
  responded_at: string;
  privacy_agreed_at: string;
}

const AdminInquiryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inquiry, setInquiry] = useState<InquiryDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState('');
  const [respondedAt, setRespondedAt] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/inquiries/${id}`, {
          credentials: 'include',
        });
        if (res.ok) {
          const json = await res.json();
          setInquiry(json.data);
          setStatus(json.data.status);

          if (json.data.responded_at) {
            // Convert to input datetime-local format YYYY-MM-DDThh:mm
            const formatted = json.data.responded_at
              .replace('/', '-')
              .replace('/', '-')
              .replace(' ', 'T');
            setRespondedAt(formatted);
          }
        }
      } catch (error) {
        console.error('Failed to fetch inquiry detail', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/inquiries/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          status,
          responded_at: respondedAt || null,
        }),
      });

      if (res.ok) {
        alert('ステータスと対応日時を更新しました。');
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Update error', error);
      alert('更新に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading)
    return (
      <div className={styles.contentBox}>
        <p>読み込み中...</p>
      </div>
    );
  if (!inquiry)
    return (
      <div className={styles.contentBox}>
        <p>お問い合わせが見つかりません。</p>
      </div>
    );

  return (
    <div className={`${styles.contentBox} ${styles.detailWrap}`}>
      <div className={styles.detailMain}>
        <div className={styles.detailHeader}>
          <div className={styles.headerLeft}>
            <button onClick={() => navigate(-1)} className={styles.backButton}>
              <img src={arrowIcon} alt="戻る" />
            </button>
            <div className={styles.headerInfo}>
              <h1>お問い合わせ詳細</h1>
              <p>ID: {inquiry.id}</p>
            </div>
          </div>
        </div>

        <div className={styles.infoBox}>
          <h2 className={styles.sectionTitle}>送信者情報</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>企業名</p>
              <p className={`${styles.infoValue} ${styles.infoValueBold}`}>{inquiry.company}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>氏名</p>
              <p className={`${styles.infoValue} ${styles.infoValueBold}`}>{inquiry.userName}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>メールアドレス</p>
              <p className={styles.infoValue}>{inquiry.email}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>電話番号</p>
              <p className={styles.infoValue}>{inquiry.phone || '-'}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>立場</p>
              <p className={styles.infoValue}>
                {inquiry.role ? ROLE_MAP[inquiry.role] || inquiry.role : '-'}
              </p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>広告ご予算</p>
              <p className={styles.infoValue}>
                {inquiry.budget ? BUDGET_MAP[inquiry.budget] || inquiry.budget : '-'}
              </p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>送信日時</p>
              <p className={styles.infoValue}>{formatDate(inquiry.date)}</p>
            </div>
            <div className={styles.infoItem}>
              <p className={styles.infoLabel}>ポリシー同意日時</p>
              <p className={styles.infoValue}>{formatDate(inquiry.privacy_agreed_at)}</p>
            </div>
          </div>
        </div>

        <div className={styles.infoBox}>
          <h2 className={styles.sectionTitle}>お問い合わせ内容</h2>
          <div className={styles.infoItem}>
            <p className={`${styles.infoValue} ${styles.inquiryMessage}`}>{inquiry.message}</p>
          </div>
        </div>
      </div>

      <div className={styles.detailSidebar}>
        <div className={styles.infoBox}>
          <h2 className={styles.sectionTitle}>管理情報</h2>

          <div className={styles.formGroup}>
            <label className={styles.infoLabel}>ステータス</label>
            <select
              className={`${styles.formInput} ${styles.formSelect}`}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="unread">未対応</option>
              <option value="in_progress">対応中</option>
              <option value="resolved">完了</option>
            </select>
          </div>

          <div className={`${styles.formGroup} ${styles.formGroupLarge}`}>
            <label className={styles.infoLabel}>対応日時</label>
            <input
              type="datetime-local"
              className={`${styles.formInput} ${styles.dateInput}`}
              value={respondedAt}
              onChange={(e) => setRespondedAt(e.target.value)}
            />
          </div>

          <button
            className={`${styles.actionButton} ${styles.actionButtonFull}`}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? '保存中...' : '管理情報を保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminInquiryDetail;
